import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from '@blocs.i18n';
import Button from '@minecraft.button';
import Pill from '@minecraft.pill';
import { media } from '@minecraft.themes';
import { SCREEN_NAMES, EditProfile, AppType } from '@blocs.common';
import { AnchorButton, UserInfoContainer, InfoHeading } from '../styles/ProfileView';
import ProfileViewContext from '../ProfileViewContext';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

const sectionName = 'assets';

const UserInfoBtnContainer = styled.div`
  background: ${(props) => props.theme.colors.accent.greyBlue2};
  padding: 1rem 1rem 1rem 3rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin: 0 1rem;
  ${media.sm`
    margin: 0;
    justify-content: flex-start;
  `}
`;

const UserInfoSection = styled.div`
  background: ${(props) => props.theme.colors.primary.white};
  padding: 1rem;
  ${media.sm`
    padding: 1rem 3rem;
  `}
`;

const UserInfoHeading = styled.h3`
  font-size: 1.7rem;
  line-height: 2rem;
`;

const StyledPill = styled(Pill)`
  padding: 0 1rem;
  margin: 0.5rem 0.5rem 0 0;
`;

const PillsContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
`;

const StylesAppreanceSubHeading = styled.h4`
  font-weight: 800;
  line-height: 0.8rem;
  margin-bottom: 0;
  font-size: 0.8rem;
`;

const UserInfoHeadingContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const arrHasItems = <T,>(value?: T[] | readonly T[]) => {
  return Array.isArray(value) && value.length > 0;
};
const Assets: FC = () => {
  const { t } = useTranslation();

  const {
    fetcher: { profile },
    isAdmin,
    onEditClick,
    isPreview,
    appType,
  } = React.useContext(ProfileViewContext);

  const areAssetsAvailable = () => {
    return (
      arrHasItems(profile.profileVehicles) ||
      arrHasItems(profile.profilePets) ||
      arrHasItems(profile.props) ||
      arrHasItems(profile.sportEquipments) ||
      arrHasItems(profile.profileWardrobes)
    );
  };

  const getVehicleDisplayText = (vehicle) => {
    const name = vehicle.vehicleName ? `${vehicle.vehicleName} ` : '';
    const make = vehicle.vehicleTypeMake.make.name ? `${vehicle.vehicleTypeMake.make.name} ` : '';
    const year = vehicle.vehicleYear ? `(${vehicle.vehicleYear})` : '';

    return make + name + year;
  };

  if (isPreview) {
    return null;
  }

  const assetsAvailableButtons = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      onClick={() => {
        onEditClick('assets');
      }}
    >
      {t('common:button.edit')}
    </AnchorButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      href={`/profile/${profile.profileId}/assets`}
    >
      {t('common:button.edit')}
    </DuoLink>
  );

  const assetsNotAvailableButtons = isAdmin ? (
    <Button
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      onClick={() => {
        onEditClick('assets');
      }}
      outlined="true"
    >
      {t('common:profile.assets.addAssets')}
    </Button>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      href={`/profile/${profile.profileId}/assets`}
    >
      <Button outlined="true">{t('common:profile.assets.addAssets')}</Button>
    </DuoLink>
  );

  const profileAppearanceInfoText =
    appType === AppType.Reps ? t('common:profile.appearanceInfoReps') : t('common:profile.appearanceInfo');

  return (
    <UserInfoContainer id="div-assets" data-qa-id="assets-container" data-testid="profile-assets-container">
      {areAssetsAvailable() ? (
        <UserInfoSection>
          <UserInfoHeadingContainer>
            <UserInfoHeading>{t('common:form.title.assets')}</UserInfoHeading>
            {assetsAvailableButtons}
          </UserInfoHeadingContainer>
          <EditProfile.CompoundAppearanceInfo data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}`}>
            {profileAppearanceInfoText}
          </EditProfile.CompoundAppearanceInfo>

          {arrHasItems(profile.profileVehicles) && (
            <>
              <StylesAppreanceSubHeading data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-vehicles`}>
                {t('common:profile.assets.vehicles')}
              </StylesAppreanceSubHeading>
              <PillsContainer>
                {profile.profileVehicles.map((vehicle) => (
                  <StyledPill
                    key={`profileVehicle-${vehicle.profileVehicleId}`}
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-vehicle-name`}
                    text={getVehicleDisplayText(vehicle)}
                  />
                ))}
              </PillsContainer>
            </>
          )}

          {arrHasItems(profile.profilePets) && (
            <>
              <StylesAppreanceSubHeading data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-pets`}>
                {t('common:profile.assets.pets')}
              </StylesAppreanceSubHeading>
              <PillsContainer>
                {profile.profilePets.map((pet) => (
                  <StyledPill
                    key={`profilePet-${pet.id}`}
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-pet-name`}
                    text={pet.name}
                  />
                ))}
              </PillsContainer>
            </>
          )}

          {arrHasItems(profile.props) && (
            <>
              <StylesAppreanceSubHeading data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-props`}>
                {t('common:profile.assets.props')}
              </StylesAppreanceSubHeading>
              <PillsContainer>
                {profile.props.map((prop) => (
                  <StyledPill
                    key={`prop-${prop.profilePropId}`}
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-prop-name`}
                    text={prop.name}
                  />
                ))}
              </PillsContainer>
            </>
          )}

          {arrHasItems(profile.sportEquipments) && (
            <>
              <StylesAppreanceSubHeading
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-sport-equipment`}
              >
                {t('common:profile.assets.sportsEquipment')}
              </StylesAppreanceSubHeading>
              <PillsContainer>
                {profile.sportEquipments.map((sportEquipment) => (
                  <StyledPill
                    key={`sportEquipment-${sportEquipment.id}`}
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-sport-equipment-name`}
                    text={sportEquipment.name}
                  />
                ))}
              </PillsContainer>
            </>
          )}

          {arrHasItems(profile.profileWardrobes) && (
            <>
              <StylesAppreanceSubHeading data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-wardrobe`}>
                {t('common:label.assets.wardrobe')}
              </StylesAppreanceSubHeading>
              <PillsContainer>
                {profile.profileWardrobes.map((wardrobe) => (
                  <StyledPill
                    key={`wardrobe-${wardrobe.id}`}
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-wardrobe-name`}
                    text={wardrobe.name}
                  />
                ))}
              </PillsContainer>
            </>
          )}
        </UserInfoSection>
      ) : (
        <div>
          <InfoHeading>{t('common:form.title.assets')}</InfoHeading>
          <UserInfoBtnContainer>{assetsNotAvailableButtons}</UserInfoBtnContainer>
        </div>
      )}
    </UserInfoContainer>
  );
};

export default Assets;
